import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import get from "lodash/get";
import {
  EmptyLayout,
  Footer,
  SearchBar,
  Table,
} from "../../components/molecules";
import {
  chevronLeftIcon,
  menuDeleteIcon,
  menuDuplicateIcon,
  menuEditIcon,
  penIcon,
  menuAltIcon,
} from "../../assets/icons";
import {
  POPOVER_BUTTONS,
  FOOTER_CONSTANTS,
  EMPTY_STRING,
  EMPTY_ARRAY,
} from "../../constants";
import { PopOver } from "../../components/atoms";
import {
  selectCollection,
  updateCollection,
  selectSelectedDataModel,
} from "./index";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import { actions } from "./dataModels.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { actions as drawerActions } from "../../components/organisms/Drawer/drawers.reducers";
import { DRAWERS } from "../../components/organisms/AppDrawers/drawers.constants";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { renderNoDataModelLayout } from "../../helpers";

const { setSelectedProperty, setSelectedCollectionId, setSelectedDataModelId } =
  actions;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { setSelectedDrawer } = drawerActions;
const { MANAGE_PROPERTY_DRAWER, MANAGE_COLLECTION_DRAWER } = DRAWERS;

const { COLLECTION_TITLE, COLLECTION_SUBTITLE } = FOOTER_CONSTANTS;
const { COLLECTION_SCHEMA_MODAL, DELETE_MODAL, CREATE_COLLECTION_MODAL } =
  MODALS;
const { setSelectedTab } = breadCrumbActions;
const { UPDATE_COLLECTION, FETCH_COLLECTION, FETCH_COLLECTIONS } =
  SPINNER_NAMES;

const Collection = () => {
  const { collectionId, dataModelId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedCollection = useSelector(selectCollection);
  const selectedDataModel = useSelector(selectSelectedDataModel);

  const [show, setShow] = useState(false);
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState(EMPTY_ARRAY);
  const [searchValue, setSearchValue] = useState(EMPTY_STRING);

  const { DELETE, DUPLICATE, EDIT } = POPOVER_BUTTONS;

  useEffect(() => {
    if (!selectedCollection && dataModelId)
      dispatch(setSelectedDataModelId(dataModelId));
  }, [dispatch, dataModelId, selectedCollection]);

  useEffect(() => {
    if (selectedCollection) {
      setSelectedProperties(
        selectedCollection.schema.properties.filter((item) =>
          item.name.toLowerCase().includes(searchValue)
        )
      );
    }
  }, [selectedCollection, searchValue]);

  useEffect(() => {
    if (collectionId) dispatch(setSelectedCollectionId(collectionId));
  }, [dispatch, collectionId]);

  useEffect(() => {
    dispatch(setSelectedTab(selectedDataModel));
  }, [dispatch, selectedDataModel]);

  const handleShouldShowDrawer = useCallback(
    () => dispatch(setSelectedModal(CREATE_COLLECTION_MODAL)),
    [dispatch]
  );

  const handleShouldShowSchema = useCallback(
    () => dispatch(setSelectedModal(COLLECTION_SCHEMA_MODAL)),
    [dispatch]
  );

  const popOverOptions = (row) => [
    {
      name: EDIT,
      icon: menuEditIcon,
      handleOnClick: () => {
        setShow(!show);
        dispatch(setSelectedProperty(row));
        dispatch(setSelectedDrawer(MANAGE_PROPERTY_DRAWER));
      },
    },
    {
      name: DUPLICATE,
      icon: menuDuplicateIcon,
      handleOnClick: () => console.log("Clicked duplicate"),
    },
    {
      name: DELETE,
      icon: menuDeleteIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => {
        dispatch(setSelectedModal(DELETE_MODAL));
        dispatch(setItemToBeDeleted(row));
        setShouldShowDeleteModal(true);
      },
    },
  ];

  const handleDeleteProperty = useCallback(
    (property) => {
      dispatch(
        updateCollection({
          id: selectedCollection.id,
          data: {
            changeSet: {
              ...selectedCollection,
              schema: { properties: [property] },
            },
            operation: "delete",
          },
        })
      ).then((res) => {
        if (res.payload) {
          dispatch(setItemToBeDeleted(null));
          dispatch(setSelectedModal(null));
          setShouldShowDeleteModal(false);
        }
      });
    },
    [dispatch, selectedCollection]
  );

  const buttonFormatter = (row) =>
    !get(row, ["meta", "isKey"]) && (
      <div onClick={(e) => e.stopPropagation()}>
        <PopOver
          options={popOverOptions(row)}
          classes="right-full -bottom-3"
          data={row}
        />
      </div>
    );

  const dataTypeFormatter = (row) => {
    return get(row, ["meta", "description"]);
  };

  const columns = [
    {
      dataField: "name",
      text: "Name",
    },
    {
      text: "Type",
      className: "text-center",
      formatter: dataTypeFormatter,
    },
    {
      width: 70,
      formatter: buttonFormatter,
    },
  ];

  const handleOnRowClick = (row) => {
    const isPrimaryKey = get(row, "meta.isKey", false);
    if (!isPrimaryKey) {
      setShow(!show);
      dispatch(setSelectedProperty(row));
      dispatch(setSelectedDrawer(MANAGE_PROPERTY_DRAWER));
    }
  };

  const renderCollection = () =>
    (searchValue ? selectedProperties.length : selectedCollection) ? (
      <>
        <Table
          data={
            searchValue
              ? selectedProperties
              : selectedCollection.schema.properties
          }
          columns={columns}
          onRowClick={handleOnRowClick}
        />
        {shouldShowDeleteModal && (
          <DeleteConfirmationModal
            title="Delete Property"
            handleDelete={handleDeleteProperty}
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            spinner={UPDATE_COLLECTION}
          />
        )}
      </>
    ) : (
      <EmptyLayout
        title="No Properties"
        subTitle="Add a new column to this object."
        buttonText="Create New Property"
        image={menuAltIcon}
        handleOnClick={handleShouldShowDrawer}
      />
    );

  return (
    <div className="content scrollbar bg-gray-900 overflow-auto p-6 flex flex-1 flex-col">
      <Helmet>
        <title>Collection | TavaJS</title>
      </Helmet>
      <div className="flex-col flex mb-4 gap-4">
        <div className="flex flex-row">
          <button
            className="h-10 rounded-md hover:bg-gray-700/60"
            onClick={() => navigate(-1)}
          >
            <img src={chevronLeftIcon} width="24" alt="chevron-left-icon" />
          </button>
          {selectedDataModel && (
            <button
              className="h-10 px-3 flex items-center rounded-md hover:bg-gray-700/60 ml-1"
              onClick={() =>
                dispatch(setSelectedDrawer(MANAGE_COLLECTION_DRAWER))
              }
            >
              <span className="text-sm font-medium text-white mr-3">
                {selectedCollection?.name}
              </span>
              <img src={penIcon} className="w-4" alt="pen-icon" />
            </button>
          )}
        </div>
        {selectedDataModel && (
          <div className="flex-col  sm:flex-row flex">
            <SearchBar
              placeholder="Filter Properties"
              handleOnSearch={(value) => setSearchValue(value.toLowerCase())}
            />
            <div className="sm:ml-auto flex flex-row mt-3 sm:mt-0">
              <button
                type="button"
                className="inline-flex justify-center items-center px-4 py-2 btn btn-primary-light w-full xs:w-auto mr-4 text-[12px] sm:text-[14px] leading-4"
                onClick={() => handleShouldShowSchema()}
              >
                Show Schema
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center px-2 sm:px-4 py-2 btn btn-primary w-full xs:w-auto text-[12px] sm:text-[14px] leading-4"
                onClick={() => handleShouldShowDrawer()}
              >
                Create Properties
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="justify-between flex flex-col flex-1">
        <Spinner name={[FETCH_COLLECTION, FETCH_COLLECTIONS]}>
          {selectedDataModel
            ? renderCollection()
            : renderNoDataModelLayout(navigate)}
        </Spinner>
        {selectedDataModel && (
          <Footer title={COLLECTION_TITLE} subtitle={COLLECTION_SUBTITLE} />
        )}
      </div>
    </div>
  );
};

export default Collection;
